import { Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import type { PropTextures } from '../render/props';
import type { Scene } from '../core/Scene';
import type { InputSnapshot } from '../input/actions';
import type { GameContext } from '../GameContext';
import { Palette, PROC_COLOR } from '../render/Palette';
import { Layer } from '../render/layers';
import { drawPlayer } from '../render/PlayerGfx';
import { drawEnemy } from '../render/EnemyGfx';
import { CameraContainer } from '../render/CameraContainer';
import { BulletRenderer } from '../render/BulletRenderer';
import { TelegraphRenderer } from '../render/TelegraphRenderer';
import { lerp, dist2 } from '../core/math';
import { EventBus } from '../core/EventBus';
import { Player } from '../sim/entities/Player';
import { World } from '../sim/World';
import { RunState, randomSeed } from '../sim/run/RunState';
import { TILE } from '../map/MapGen';
import { CHUNK_ARCHETYPES, generateChunkMap, type ChunkArchetype } from '../map/ChunkGen';
import { generateVaultMap, VAULT_LAYOUTS, type VaultLayout } from '../map/VaultGen';
import { TileMap } from '../map/TileMap';
import { buildMapGraphics, drawExitHatch, ExitPortal } from '../map/MapRenderer';
import { themeFor } from '../render/themes';
import { Minimap } from '../ui/Minimap';
import { DebugFlags } from '../debug/DebugFlags';
import { EndScene, formatRunTime } from './EndScene';
import { LevelUpOverlay } from './LevelUpOverlay';
import { ArtifactSwapOverlay } from './ArtifactSwapOverlay';
import {
  applyDescentBank, applyRunEnd, emptyBankSummary, mergeBankSummary, type RunEndSummary,
} from '../sim/meta/runEnd';
import { usePocket } from '../sim/run/pocket';
import { ShopOverlay } from './ShopOverlay';
import { PauseOverlay } from './PauseOverlay';
import { RunOverviewOverlay } from './RunOverviewOverlay';
import { MenuScene } from './MenuScene';
import { generateShopStock, type ShopItem } from '../sim/run/shop';
import {
  PICK_FRACTIONS, RUN_TIME_LIMIT_SECONDS, gemMultiplier, picksEarned, summonBarFill,
} from '../sim/run/progression';
import { PICKUP_BUMP_CAP, bumpThreshold } from '../sim/run/pickupTrack';
import { metaStatModifiers } from '../sim/meta/metaUpgrades';
import { isCharacterId } from '../sim/characters/CharacterDefs';
import {
  HOUND_HP, IRONBOUND_AURA_RADIUS, IRONBOUND_STACK_SECONDS,
  aspectDescendHeal, aspectHealBlock,
} from '../sim/characters/aspects';
import { logJournal } from '../meta/journal';
import { activeStatus } from '../sim/characters/actives';
import { maxUnlockedTier, tierLabel } from '../sim/run/tiers';
import { rollOffers, applyOffer, rerollCost } from '../sim/run/skillOffers';
import { SHRED_MAX_STACKS, skillOnFloorEnter } from '../sim/abilities/skillHooks';
import { AbilitySystem } from '../sim/abilities/AbilitySystem';
import { ABILITY_DEFS, ABILITY_SLOTS, skillParams } from '../sim/abilities/AbilityDefs';
import { TAU } from '../core/math';
import { ArtifactSystem } from '../sim/artifacts/ArtifactSystem';
import { ARTIFACTS_BY_ID } from '../sim/artifacts/ArtifactDefs';
import { CURSES_BY_ID } from '../sim/artifacts/CurseDefs';
import { acceptShrine } from '../sim/events/floorEvents';
import { SHRINE_BOONS_BY_ID, type FloorEvent, type FloorEventType } from '../sim/events/EventDefs';
import { ShrineOverlay } from './ShrineOverlay';
import { CleanseOverlay } from './CleanseOverlay';
import type { Rng } from '../core/rng/Rng';

/** Default gameplay camera zoom (Sarah, July 2026). ?zoom= overrides; 0 resets here. */
const GAME_ZOOM = 1.3;

const AFFIX_COLOR = {
  fast: 0xffe14d,
  shielded: 0x9db4ff,
  gemRich: Palette.gem,
  curseTouched: 0xb56bff,
} as const;

interface EffectRing {
  x: number;
  y: number;
  radius: number;
  age: number;
  maxAge: number;
  color: number;
}

const EXIT_RANGE = 34;
/** Round 5 A/B (Sarah): HUD corner minimap hidden — the map lives enlarged
 *  on the pause screen. Flip to true to bring the corner map back. */
export const HUD_MINIMAP = false;
/** First-sight flavor toast distance for floor events. */
const EVENT_SEEN_RANGE = 150;

// An ambush IS a chest until opened — marker, color, toast, and hint must be
// indistinguishable from lootChest or the gamble telegraphs itself.
const CHEST_SEEN_TOAST = 'An old chest sits in the gloom';

const EVENT_COLOR: Record<FloorEventType, number> = {
  merchant: Palette.gold,
  lootChest: Palette.gem,
  ambush: Palette.gem,
  shrine: 0xb56bff,
  vaultDoor: Palette.playerCool,
  boneKey: Palette.gold,
  strongbox: Palette.gold,
  brazierRelay: Palette.gold,
  spring: Palette.gem,
};

const EVENT_SEEN_TOAST: Record<FloorEventType, string> = {
  merchant: 'A wayside merchant beckons',
  lootChest: CHEST_SEEN_TOAST,
  ambush: CHEST_SEEN_TOAST,
  shrine: 'A cursed shrine hums nearby',
  vaultDoor: 'A sealed reliquary door stands in the dark',
  boneKey: 'A bone key gleams on the stones',
  strongbox: 'A banded strongbox — its lock wants a key',
  brazierRelay: 'Cold braziers stand in the dark',
  spring: 'A clear spring murmurs here',
};

/** Consumed events dim and stop offering interaction. */
function eventConsumed(ev: FloorEvent): boolean {
  switch (ev.type) {
    case 'lootChest': return ev.opened;
    case 'ambush': return ev.triggered;
    case 'shrine': return ev.taken;
    case 'boneKey': return ev.taken;
    case 'strongbox': return ev.opened;
    case 'brazierRelay': return ev.chest.opened;
    case 'spring': return ev.used;
    default: return false; // merchant + vault door stay usable
  }
}

/**
 * The run. RunState + Player persist across floors; the World (and its
 * renderers) is rebuilt on every descent.
 */
export class RunScene implements Scene {
  readonly root = new Container();
  readonly blocksUpdateBelow = true;

  private camera = new CameraContainer();
  private hud = new Container();
  private runState: RunState;
  private player = new Player();
  private world!: World;
  private events = new EventBus();

  // Per-floor display objects, torn down on descent.
  private floorLayer = new Container();
  private renderers: BulletRenderer[] = [];
  private telegraphRenderer!: TelegraphRenderer;
  private enemyGfx = new Map<number, Graphics>();
  private enemyLayer = new Container();
  private playerGfx = drawPlayer();
  private exitGfx: Container = drawExitHatch();
  private shopGfx: Container | null = null;
  private shopStock: ShopItem[] = [];
  private minimap: Minimap | null = null;
  /** Cartographer's Sketch applied to THIS floor's minimap already. */
  private intelApplied = false;

  private floorText: Text;
  private seedText: Text;
  private hintText: Text;
  private gemText: Text;
  private heartsGfx = new Graphics();
  private lastHp = -1;
  /** Aspect status widgets (Wick candle vignette + per-aspect pips/dials). */
  private vignetteGfx = new Graphics();
  private aspectGfx = new Graphics();

  private playerFlash = 0;
  private deathDelay = -1;
  private time = 0;
  /** Ladder records only count clean runs (mirrors the god-tithe guard). */
  private readonly recordEligible =
    !DebugFlags.god && DebugFlags.floor === null &&
    DebugFlags.grant === null && DebugFlags.tier === null &&
    DebugFlags.char === null && DebugFlags.boss === null &&
    DebugFlags.mapgen === null && DebugFlags.vaultLayout === null;
  private lootRng!: Rng;
  private effectRings: EffectRing[] = [];
  /** Leech Chain flashes; render-only, jitter via this.time. */
  private lightningBolts: {
    points: { x: number; y: number }[]; age: number; maxAge: number;
    kind: 'leech' | 'arc';
  }[] = [];
  /** Cask's Shortcut fuse channel (seconds held). */
  private fuseChannel = 0;
  /** Bram's kneel-at-the-body revive channel (seconds held). */
  private reviveChannel = 0;
  private effectGfx = new Graphics();
  private wardGfx = new Graphics();
  private dropsGfx = new Graphics();
  private slotsGfx = new Container();
  private slotsKey = '';
  private artifactSystem: ArtifactSystem;
  private artifactStrip = new Container();
  private artifactStripKey = '';
  private toasts: { text: Text; age: number }[] = [];
  private summonGfx = new Graphics();
  private bossBarGfx = new Graphics();
  private bossNameText: Text;
  private barPctText!: Text;
  /** Run-timer countdown (goes overtime-red past RUN_TIME_LIMIT_SECONDS). */
  private timerText: Text;
  private bumpText: Text;
  private circleGfx = new Graphics();
  private winDelay = -1;

  private swapPromptOpen = false;
  /** Run-wide banking accumulation (descent checkpoints) — display only. */
  private runBanked = emptyBankSummary();

  /** Floor-event markers (rebuilt per floor; gfx lives in floorLayer). */
  /** Floating damage numbers: pooled Texts over the sim's aggregated feed. */
  private dmgPopups: { text: Text; age: number; max: number }[] = [];
  private dmgPopupPool: Text[] = [];
  /** `sub` = brazier index for brazierRelay records (one marker per brazier). */
  private eventGfx: { ev: FloorEvent; gfx: Container; seen: boolean; sub: number | undefined }[] = [];
  private geodeGfx: { g: { broken: boolean }; gfx: Container }[] = [];

  // ---- Vault ("Reliquary") transition state. inVault ⇔ originStash !== null.
  /** Per-floor vault cache: keeps the Keeper dead / altar spent on re-entry. */
  private vaultWorld: World | null = null;
  private vaultMinimap: Minimap | null = null;
  private originStash: {
    world: World;
    minimap: Minimap;
    shopStock: ShopItem[];
    returnX: number;
    returnY: number;
  } | null = null;
  private altarGfx: Container | null = null;
  private vaultChestGfx: Container | null = null;

  constructor(private readonly ctx: GameContext, seed?: string) {
    const profile = ctx.profile;
    // ?tier= forces a ladder tier past the unlock chain (testing); normal runs
    // use the sticky pick clamped to what's actually unlocked.
    const tier = DebugFlags.tier !== null
      ? Math.max(0, Math.floor(DebugFlags.tier))
      : Math.min(profile.data.lastTier, maxUnlockedTier(profile.data));
    this.runState = new RunState(seed ?? DebugFlags.seed ?? randomSeed(), {
      tier,
      broughtArtifacts: profile.loadout.map((id) => ({
        artifactId: id,
        quality: profile.ownedQuality(id) ?? 1,
      })),
      artifactCapacity: profile.artifactCapacity,
      metaStatMods: metaStatModifiers(profile.data),
      pocket: profile.data.unlocks.startingMend ? 'mend' : null,
      character: DebugFlags.char !== null && isCharacterId(DebugFlags.char)
        ? DebugFlags.char
        : profile.lastCharacter,
    });
    this.runState.god = DebugFlags.god;
    this.camera.zoom = DebugFlags.zoom ?? GAME_ZOOM;
    if (DebugFlags.floor) this.runState.floor = DebugFlags.floor;
    profile.recordRunStart();

    this.root.addChild(this.camera);
    this.camera.sortableChildren = true;
    this.root.addChild(this.hud);

    this.floorText = makeText(16, Palette.hudText);
    this.floorText.anchor.set(1, 0);
    this.seedText = makeText(11, Palette.hudDim);
    this.seedText.anchor.set(1, 0);
    this.seedText.text = `seed ${this.runState.seed}`;
    this.gemText = makeText(15, Palette.gem);
    this.hintText = makeText(13, Palette.gold);
    this.hintText.anchor.set(0.5, 1);
    // Vignette sits under every other HUD element; aspect widgets above it.
    this.hud.addChildAt(this.vignetteGfx, 0);
    this.hud.addChild(this.floorText, this.seedText, this.gemText, this.heartsGfx,
      this.aspectGfx, this.hintText);

    this.events.on('playerHit', () => {
      this.playerFlash = 4;
      this.camera.addTrauma(0.45);
      this.ctx.loop.hitstop(0.09);
    });
    this.events.on('enemyKilled', () => {
      this.camera.addTrauma(0.06);
    });
    this.events.on('statBump', ({ label }) => {
      // Boss-win vacuum credit can still fire bumps — skip the noise then.
      if (!this.world.bossDefeated) this.pushToast(`⬦ ${label}`, Palette.gem);
    });
    this.events.on('pickupHealed', ({ amount }) => {
      if (!this.world.bossDefeated) this.pushToast(`⬦ the gems mend  +${amount} HP`, Palette.gem);
    });
    this.events.on('burstFired', (e) => {
      this.effectRings.push({ ...e, age: 0, maxAge: 0.45, color: Palette.playerCoolSoft });
      this.camera.addTrauma(0.12);
    });
    this.events.on('splashBurst', (e) => {
      this.effectRings.push({ ...e, age: 0, maxAge: 0.35, color: Palette.enemyHotAlt });
    });
    this.events.on('debrisImpact', (e) => {
      this.effectRings.push({ ...e, age: 0, maxAge: 0.4, color: Palette.debris });
      this.camera.addTrauma(0.08);
    });
    this.events.on('chainLash', ({ points, kind }) => {
      this.lightningBolts.push({ points, age: 0, maxAge: 0.18, kind: kind ?? 'leech' });
    });
    this.events.on('bellringDischarged', (e) => {
      this.effectRings.push({ x: e.x, y: e.y, radius: e.radius, age: 0, maxAge: 0.5, color: Palette.gold });
      this.camera.addTrauma(0.1 + 0.04 * e.stacks);
    });
    this.events.on('flareIgnited', ({ x, y, seconds }) => {
      this.effectRings.push({ x, y, radius: 70, age: 0, maxAge: 0.4, color: Palette.enemyHotAlt });
      this.pushToast(`The flame roars — ${seconds}s of fury`, Palette.gold);
    });
    this.events.on('crawlersEnraged', () => {
      this.pushToast('The swarm is whipped to fury', Palette.gold);
    });
    this.events.on('ninefoldSaved', () => {
      this.pushToast('Death refused — descend to renew it', Palette.gold);
      this.camera.addTrauma(0.4);
    });
    this.events.on('candleEmpty', () => {
      this.pushToast('The candle gutters — it burns YOU now', Palette.enemyHot);
    });
    this.events.on('houndDied', ({ x, y }) => {
      this.pushToast("Gaunt falls — the floor's unbanked gems die with him", Palette.enemyHot);
      this.camera.addTrauma(0.6);
      // Point Bram at the body — the revive is the way out of the dead state.
      this.minimap?.setBody({ x: Math.floor(x / TILE), y: Math.floor(y / TILE) });
    });
    this.events.on('houndRevived', () => {
      this.pushToast('Gaunt takes the leash again', Palette.gold);
      this.minimap?.setBody(null);
    });
    this.events.on('sunderStarted', () => {
      this.pushToast('Sundered — untouchable, threefold, empty-handed', Palette.playerCool);
    });
    this.events.on('knellToll', (e) => {
      this.effectRings.push({ ...e, radius: 60, age: 0, maxAge: 0.5, color: Palette.gem });
    });
    this.events.on('thresholdShock', (e) => {
      this.effectRings.push({ ...e, age: 0, maxAge: 0.5, color: Palette.gold });
      this.camera.addTrauma(0.2);
    });
    this.events.on('corpseDetonated', (e) => {
      this.effectRings.push({ ...e, age: 0, maxAge: 0.35, color: Palette.enemyHotAlt });
    });
    this.events.on('causticFed', (e) => {
      // A fed pool visibly reacts — the "where's my puddle" answer.
      this.effectRings.push({ ...e, age: 0, maxAge: 0.3, color: Palette.gem });
    });
    this.events.on('novaFired', (e) => {
      this.effectRings.push({ ...e, age: 0, maxAge: 0.4, color: Palette.gold });
      this.camera.addTrauma(0.15);
    });
    this.events.on('undertowPulse', (e) => {
      this.effectRings.push({ ...e, age: 0, maxAge: 0.4, color: Palette.playerCool });
    });
    this.events.on('rimePulse', (e) => {
      if (!e.hit) return;
      this.effectRings.push({ ...e, age: 0, maxAge: 0.45, color: Palette.playerCoolSoft });
    });
    this.events.on('shatter', (e) => {
      this.effectRings.push({ ...e, radius: 26, age: 0, maxAge: 0.3, color: Palette.playerCoolSoft });
    });
    this.events.on('leechPulse', (e) => {
      if (!e.healed) return;
      this.effectRings.push({ ...e, age: 0, maxAge: 0.3, color: Palette.enemyHotAlt });
    });
    this.hud.addChild(this.slotsGfx, this.artifactStrip, this.summonGfx, this.bossBarGfx);
    this.bossNameText = makeText(14, Palette.enemyHot);
    this.bossNameText.anchor.set(0.5, 0);
    this.bossNameText.visible = false;
    this.hud.addChild(this.bossNameText);
    this.barPctText = makeText(12, Palette.hudText);
    this.barPctText.anchor.set(0.5, 0.5);
    this.barPctText.visible = false;
    this.hud.addChild(this.barPctText);
    this.timerText = makeText(15, Palette.hudText);
    this.timerText.anchor.set(0.5, 0);
    this.hud.addChild(this.timerText);
    this.bumpText = makeText(11, Palette.gem);
    this.bumpText.anchor.set(0, 0.5);
    this.hud.addChild(this.bumpText);

    this.events.on('circleAppeared', ({ x, y }) => {
      this.pushToast('The summoning circle has formed', Palette.enemyHot);
      this.camera.addTrauma(0.3);
      // Point the way on the minimap (not fog-gated — the toast announced it).
      this.minimap?.setCircle({ x: Math.floor(x / TILE), y: Math.floor(y / TILE) });
    });
    this.events.on('bossStarted', ({ name }) => {
      this.pushToast(`${name} answers`, Palette.enemyHot);
      this.camera.addTrauma(0.6);
      this.ctx.loop.hitstop(0.15);
      this.minimap?.setCircle(null); // circle consumed by the summon
    });
    this.events.on('bossPhaseChanged', () => {
      this.pushToast(this.world.bossDef?.stirLine ?? 'It stirs…', Palette.enemyHotAlt);
      this.camera.addTrauma(0.5);
      this.ctx.loop.hitstop(0.12);
    });
    this.events.on('bossKilled', () => {
      this.camera.addTrauma(1);
      this.ctx.loop.hitstop(0.3);
      this.winDelay = 110;
    });
    this.events.on('ambushTriggered', () => {
      this.pushToast("It's a trap!", Palette.enemyHot);
      this.camera.addTrauma(0.5);
    });
    this.events.on('enrageStarted', () => {
      this.pushToast('The dark closes in — it hunts you now', Palette.enemyHot);
      this.camera.addTrauma(0.5);
      this.ctx.loop.hitstop(0.12);
    });
    this.events.on('chestOpened', ({ artifactId, content }) => {
      if (content === 'mend') this.pushToast('A Mend settles into your pocket', Palette.gem);
      else if (content === 'gems') this.pushToast('Gems spill out', Palette.gem);
      else if (artifactId === null) this.pushToast('The chest is bare — gems spill out', Palette.hudDim);
    });
    this.events.on('wardBlocked', ({ chargesLeft }) => {
      this.pushToast(
        chargesLeft > 0 ? 'The wardstone cracks' : 'The wardstone shatters',
        Palette.playerCool,
      );
      this.camera.addTrauma(0.2);
    });
    this.events.on('keyTaken', () => {
      this.pushToast('The bone key — something on this floor is locked', Palette.gold);
    });
    this.events.on('brazierLit', ({ lit, total, window }) => {
      this.pushToast(`The brazier roars — ${lit}/${total}, ${Math.ceil(window)}s`, Palette.gold);
    });
    this.events.on('relayReset', () => {
      this.pushToast('The braziers gutter out', Palette.hudDim);
    });
    this.events.on('relayUnsealed', () => {
      this.pushToast('The reward chest unseals with a crack', Palette.gold);
      this.camera.addTrauma(0.25);
    });
    this.events.on('springUsed', ({ healed }) => {
      this.pushToast(
        healed > 0 ? 'The spring knits your wounds' : 'The spring finds nothing to mend',
        healed > 0 ? Palette.gem : Palette.hudDim,
      );
    });
    this.events.on('shrineTaken', ({ boonId, curseId }) => {
      const boon = SHRINE_BOONS_BY_ID.get(boonId);
      const curse = CURSES_BY_ID.get(curseId);
      if (boon) this.pushToast(`✦ ${boon.name} — ${boon.desc}`, Palette.gold);
      if (curse) this.pushToast(`${curse.name} — ${curse.desc} (until cleansed)`, Palette.enemyHot);
    });
    this.events.on('keeperAwakened', () => {
      this.pushToast('THE KEEPER wakes', Palette.gold);
      this.camera.addTrauma(0.5);
      this.ctx.loop.hitstop(0.12);
    });
    this.events.on('goblinPanicked', () => {
      this.pushToast('It bolts — the jingling flees!', Palette.gold);
    });
    this.events.on('goblinEscaped', () => {
      this.pushToast('The jingling fades away…', Palette.hudDim);
    });
    this.events.on('goblinKilled', () => {
      this.pushToast('It bursts — treasure spills out', Palette.gold);
    });
    this.events.on('keeperKilled', () => {
      this.pushToast(
        this.world.vaultContent === 'chest'
          ? 'The Keeper falls — the chest unseals'
          : 'The Keeper falls — the altar glows',
        Palette.gold,
      );
      this.camera.addTrauma(0.6);
      this.ctx.loop.hitstop(0.15);
    });
    this.events.on('altarChannelComplete', () => {
      const world = this.world;
      this.ctx.scenes.push(new CleanseOverlay(
        this.ctx,
        this.runState,
        (artifactId, quality) => this.artifactSystem.improve(artifactId, quality, 'none', null),
        () => {
          world.altarUsed = true;
          this.pushToast('✦ cleansed — the boon remains', Palette.playerCool);
        },
      ));
    });

    this.artifactSystem = new ArtifactSystem(this.runState, this.events);
    this.events.on('artifactPickedUp', ({ artifactId, quality, proc, curseId }) => {
      this.artifactSystem.acquire(artifactId, quality, proc, curseId);
    });
    this.events.on('artifactAcquired', ({ artifactId, proc, curseId, quality }) => {
      const def = ARTIFACTS_BY_ID.get(artifactId);
      if (!def) return;
      const curse = curseId !== null ? CURSES_BY_ID.get(curseId) : undefined;
      const qLabel = `  Q ${Math.round(quality * 100)}%`;
      const suffix = proc === 'blessed'
        ? ' — boon doubled'
        : curse !== undefined ? ` — ${curse.name}` : '';
      const prefix = proc === 'none' ? '' : `${proc.toUpperCase()} `;
      this.pushToast(`◈ ${prefix}${def.name}${qLabel}${suffix}`, PROC_COLOR[proc]);
      this.pushToast(
        curse !== undefined ? `${def.desc} / ${curse.name}: ${curse.desc}` : def.desc,
        Palette.hudDim,
      );
    });
    this.events.on('artifactImproved', ({ artifactId, from, to, proc, curseId }) => {
      this.artifactSystem.improve(artifactId, to, proc, curseId);
      const def = ARTIFACTS_BY_ID.get(artifactId);
      this.pushToast(
        `◈ ${def?.name ?? artifactId} improved  ${Math.round(from * 100)}% → ${Math.round(to * 100)}%`,
        Palette.playerCool,
      );
    });
    this.events.on('artifactDust', ({ artifactId }) => {
      const def = ARTIFACTS_BY_ID.get(artifactId);
      this.pushToast(`${def?.name ?? artifactId} — inferior, turns to dust`, Palette.hudDim);
    });
    this.events.on('artifactSwapPrompt', (offered) => {
      if (this.swapPromptOpen) return;
      this.swapPromptOpen = true;
      this.ctx.scenes.push(new ArtifactSwapOverlay(
        this.ctx,
        this.runState,
        offered,
        (oldId) => {
          // Swap: unequip old (drops at the player's feet, suppressed),
          // then take the offered drop.
          const idx = this.world.artifactDrops.findIndex(
            (d) => d.artifactId === offered.artifactId && d.suppressed,
          );
          const old = this.runState.artifacts.find((a) => a.artifactId === oldId);
          const oldQuality = old?.quality ?? 1;
          const oldProc = old?.proc ?? 'none';
          const oldCurseId = old?.curseId ?? null;
          this.artifactSystem.remove(oldId);
          if (idx >= 0) this.world.artifactDrops.splice(idx, 1);
          this.artifactSystem.acquire(offered.artifactId, offered.quality, offered.proc, offered.curseId);
          // The swapped-out copy keeps its proc on the ground.
          this.world.artifactDrops.push({
            x: this.player.x, y: this.player.y,
            artifactId: oldId, quality: oldQuality,
            proc: oldProc, curseId: oldCurseId, suppressed: true,
          });
          this.swapPromptOpen = false;
        },
        () => {
          this.swapPromptOpen = false;
        },
        () => this.ctx.scenes.pop(),
      ));
    });
    for (const brought of this.runState.broughtIds) {
      this.artifactSystem.acquire(brought, this.ctx.profile.ownedQuality(brought) ?? 1);
    }
    if (DebugFlags.grant) this.artifactSystem.acquire(DebugFlags.grant, 1);

    this.loadFloor();

    this.ctx.perf.extra = () =>
      `bullets ${this.world.enemyBullets.count}  shots ${this.world.playerShots.count}  ` +
      `enemies ${this.world.enemies.length}  gems ${this.world.gems.count}`;

    if (import.meta.env.DEV) {
      (window as unknown as Record<string, unknown>)['__run'] = this;
    }
  }

  /** Live zoom tweak: - / = step by 0.1, 0 resets. HUD is unaffected. */
  private readonly onZoomKey = (e: KeyboardEvent): void => {
    const step = e.code === 'Minus' ? -0.1 : e.code === 'Equal' ? 0.1 : null;
    if (step === null && e.code !== 'Digit0') return;
    this.camera.zoom = step === null ? GAME_ZOOM : this.camera.zoom + step;
    this.pushToast(`zoom ×${this.camera.zoom.toFixed(2)}`, Palette.hudDim);
  };

  enter(): void {
    window.addEventListener('keydown', this.onZoomKey);
  }

  exit(): void {
    window.removeEventListener('keydown', this.onZoomKey);
    // Menu scenes assume the boot background.
    this.ctx.app.renderer.background.color = Palette.bg;
  }

  /** The one real-descent path (exit hatch AND Cask's fuse) — never the vault. */
  private descend(): void {
    const rs = this.runState;
    this.settleDescent();
    rs.floor++;
    rs.clearFloorBuffs();
    rs.onDescend();
    // Ninefold (Vane): descent is her heal (aspects.ts owns the rule).
    if (aspectDescendHeal(rs, this.player)) {
      this.pushToast('Her ledger clears — made whole', Palette.playerCool);
    }
    this.events.emit('floorEntered', { floor: rs.floor });
    this.loadFloor();
  }

  private loadFloor(): void {
    const rs = this.runState;
    // A real descent invalidates the previous floor's vault cache and any
    // (impossible in practice) in-vault state.
    this.vaultWorld = null;
    this.vaultMinimap = null;
    this.originStash = null;

    // Chunk-stitched floors are the default (MAPGEN_PLAN P4);
    // ?mapgen=<archetype> forces a layout (record-ineligible dev handle).
    const mg = DebugFlags.mapgen;
    const map = generateChunkMap(
      rs.streams.stream('map', rs.floor),
      mg !== null && (CHUNK_ARCHETYPES as readonly string[]).includes(mg)
        ? mg as ChunkArchetype
        : undefined,
    );
    const tileMap = new TileMap(map);

    this.player.place(TileMap.toWorld(map.entrance.x), TileMap.toWorld(map.entrance.y));
    this.player.moveResolver = (px, py, nx, ny, r) => tileMap.resolveMove(px, py, nx, ny, r);
    this.world = new World(map, tileMap, rs.floor, this.player, rs, this.events, {
      forceVaultDoor: DebugFlags.vault,
      forceBoss: DebugFlags.boss ?? undefined,
    });

    // Level-up offers draw from their own stream so their consumption can
    // never perturb the pre-rolled elite loot order.
    this.lootRng = rs.streams.stream('levelups', rs.floor);
    this.artifactSystem.setWorld(this.world);
    // Arrival skill hooks (Threshold-class). loadFloor never runs for the
    // vault, so this can't fire on vault enter/exit.
    skillOnFloorEnter(this.world);

    // Entrance shop, floors 2+ — unmissable at spawn, seeded stock. Heal
    // stock is composed by heal-block (draw-free — see shop.ts contract).
    this.shopStock = rs.floor >= 2
      ? generateShopStock(rs.streams.stream('shop', rs.floor), rs.floor, aspectHealBlock(rs))
      : [];
    this.intelApplied = false;

    this.buildFloorView({ vault: false });
    this.setMinimap(new Minimap(
      map, rs.floor >= 2,
      [
        ...this.world.floorEvents.flatMap((ev) =>
          (ev.type === 'brazierRelay' ? ev.braziers : [ev.pos]).map((pos) => ({
            x: pos.x, y: pos.y, color: EVENT_COLOR[ev.type],
            // The Reliquary door shows from floor start: 30%-rare content
            // shouldn't die to fog on the bigger chunk floors.
            always: ev.type === 'vaultDoor',
          }))),
        // Geodes deliberately NOT on the minimap (Sarah, 2026-07-28) — they
        // are ambient scenery, not objectives; the Sketch reveals events only.
      ],
      rs.character === 'wick', // Wick sees the exit from floor start
    ));

    this.floorText.text = `floor ${rs.floor}${rs.tier > 0 ? `  ·  ${tierLabel(rs.tier).toLowerCase()}` : ''}`;
    if (rs.floor > 1) {
      this.pushToast(`Floor ${rs.floor} — gems ×${gemMultiplier(rs.floor)}`, Palette.gold);
    }
    // Treasure goblin hint: a state read, not an event — the World ctor
    // rolls the goblin before this scene could subscribe to a spawn event.
    if (this.world.enemies.some((e) => e.type === 'goblin')) {
      this.pushToast('You hear a faint jingling…', Palette.gold);
    }

    // The new World is fully current — arrival-effect artifact hooks fire
    // now (floorEntered fires pre-swap and would hit the departing floor).
    this.events.emit('floorReady', { floor: rs.floor });
    logJournal(`run: floor ${rs.floor} (${map.archetype}) as ${rs.character}`);
  }

  /**
   * (Re)build the per-floor display tree against the CURRENT this.world.
   * Shared by descents and vault enter/exit: renderers bind to the world's
   * pools by reference at construction, so swapping worlds just means
   * rebuilding this view — sim state is untouched.
   */
  private buildFloorView(opts: { vault: boolean }): void {
    const map = this.world.map;

    this.camera.removeChild(this.floorLayer);
    this.floorLayer.destroy({ children: true });
    this.floorLayer = new Container();
    this.floorLayer.sortableChildren = true;
    this.camera.addChild(this.floorLayer);

    // Biome theme (ME1): tile colors + the letterbox behind the map both
    // follow the floor's archetype, with a subtle depth darkening.
    const theme = themeFor(map.archetype, this.runState.floor);
    this.ctx.app.renderer.background.color = theme.bg;
    const mapGfx = buildMapGraphics(map, theme);
    mapGfx.zIndex = Layer.Floor;
    this.floorLayer.addChild(mapGfx);

    this.telegraphRenderer = new TelegraphRenderer(this.world.telegraphs);
    this.telegraphRenderer.gfx.zIndex = Layer.Telegraphs;
    this.floorLayer.addChild(this.telegraphRenderer.gfx);

    const frames = this.ctx.textures.frames;
    const shots = new BulletRenderer(this.world.playerShots, frames);
    shots.container.zIndex = Layer.PlayerFx;
    const gems = new BulletRenderer(this.world.gems, frames);
    gems.container.zIndex = Layer.Pickups;
    const enemyBullets = new BulletRenderer(this.world.enemyBullets, frames);
    enemyBullets.container.zIndex = Layer.EnemyBullets;
    const particles = new BulletRenderer(this.world.particles, frames, true);
    particles.container.zIndex = Layer.Fx;
    this.renderers = [shots, gems, enemyBullets, particles];
    for (const r of this.renderers) this.floorLayer.addChild(r.container);

    this.effectGfx = new Graphics();
    this.effectGfx.zIndex = Layer.PlayerFx;
    this.floorLayer.addChild(this.effectGfx);
    this.wardGfx = new Graphics();
    this.wardGfx.zIndex = Layer.PlayerFx;
    this.floorLayer.addChild(this.wardGfx);
    this.dropsGfx = new Graphics();
    this.dropsGfx.zIndex = Layer.Pickups;
    this.floorLayer.addChild(this.dropsGfx);
    this.effectRings.length = 0;
    this.lightningBolts.length = 0;
    this.fuseChannel = 0;
    this.reviveChannel = 0;

    this.circleGfx = new Graphics();
    this.circleGfx.zIndex = Layer.Pickups;
    this.floorLayer.addChild(this.circleGfx);

    this.enemyGfx.clear();
    this.enemyLayer = new Container();
    this.enemyLayer.zIndex = Layer.Entities;
    this.floorLayer.addChild(this.enemyLayer);

    // In the vault, the "exit" tile is the return door.
    this.exitGfx = opts.vault ? drawEventMarker('vaultDoor', this.ctx.props) : drawExitHatch();
    this.exitGfx.zIndex = Layer.Pickups;
    this.exitGfx.position.set(TileMap.toWorld(map.exit.x), TileMap.toWorld(map.exit.y));
    this.floorLayer.addChild(this.exitGfx);

    if (!opts.vault && this.shopStock.length > 0) {
      this.shopGfx = drawShop(this.ctx.props);
      this.shopGfx.zIndex = Layer.Pickups;
      this.shopGfx.position.set(TileMap.toWorld(map.shop.x), TileMap.toWorld(map.shop.y));
      this.floorLayer.addChild(this.shopGfx);
    } else {
      this.shopGfx = null;
    }

    // Seed-placed floor events: markers + interaction anchors (empty in vault).
    // A brazier relay gets one record PER brazier (sub = index); only sub 0
    // may toast on first sight, so the trio doesn't triple-announce.
    this.eventGfx = this.world.floorEvents.flatMap((ev) => {
      const positions = ev.type === 'brazierRelay' ? ev.braziers : [ev.pos];
      return positions.map((pos, sub) => {
        const gfx = drawEventMarker(ev.type, this.ctx.props);
        gfx.zIndex = Layer.Pickups;
        gfx.position.set(TileMap.toWorld(pos.x), TileMap.toWorld(pos.y));
        this.floorLayer.addChild(gfx);
        return { ev, gfx, seen: sub > 0, sub: ev.type === 'brazierRelay' ? sub : undefined };
      });
    });

    // Geodes: scenery markers synced to sim state in render().
    this.geodeGfx = this.world.geodes.map((g) => {
      const gfx = drawGeode(this.ctx.props);
      gfx.zIndex = Layer.Pickups;
      gfx.position.set(g.x, g.y);
      this.floorLayer.addChild(gfx);
      return { g, gfx };
    });

    // Hazard emitters (ME4): static markers — the telegraphs do the warning.
    for (const hz of this.world.hazards) {
      const gfx = hz.kind === 'vent'
        ? drawVentGrate(this.ctx.props)
        : drawDebrisMarker(hz.angle, hz.halfLen, this.ctx.props);
      gfx.zIndex = Layer.Floor + 1;
      gfx.position.set(hz.x, hz.y);
      this.floorLayer.addChild(gfx);
    }

    // The vault's alcove: cleansing altar or sealed chest (vaultContent).
    this.altarGfx = null;
    this.vaultChestGfx = null;
    if (opts.vault && this.world.altarPos) {
      this.altarGfx = drawAltar(this.ctx.props);
      this.altarGfx.zIndex = Layer.Pickups;
      this.altarGfx.position.set(this.world.altarPos.x, this.world.altarPos.y);
      this.floorLayer.addChild(this.altarGfx);
    }
    if (opts.vault && this.world.vaultChest) {
      const g = drawEventMarker('lootChest', this.ctx.props);
      g.zIndex = Layer.Pickups;
      g.position.set(this.world.vaultChest.x, this.world.vaultChest.y);
      g.alpha = this.world.vaultChest.opened ? 0.35 : 1;
      this.floorLayer.addChild(g);
      this.vaultChestGfx = g;
    }

    this.playerGfx = drawPlayer();
    this.playerGfx.zIndex = Layer.Entities;
    this.floorLayer.addChild(this.playerGfx);

    // Damage-number Texts died with the old floorLayer — reset the pool.
    this.dmgPopups.length = 0;
    this.dmgPopupPool.length = 0;
  }

  /**
   * Damage numbers (2026-07-27): drain the sim's aggregated feed into
   * pooled Texts that rise and fade. World merges dot ticks per enemy, so
   * the worst case here is a few dozen live Texts, not thousands.
   */
  private syncDamagePopups(dt: number): void {
    for (let i = this.dmgPopups.length - 1; i >= 0; i--) {
      const p = this.dmgPopups[i]!;
      p.age += dt;
      if (p.age >= p.max) {
        p.text.visible = false;
        this.dmgPopupPool.push(p.text);
        this.dmgPopups.splice(i, 1);
        continue;
      }
      p.text.y -= 46 * dt;
      const fadeFrom = p.max * 0.55;
      p.text.alpha = p.age < fadeFrom ? 1 : 1 - (p.age - fadeFrom) / (p.max - fadeFrom);
    }
    const feed = this.world.damagePopups;
    for (const d of feed) {
      if (this.dmgPopups.length >= 40) break; // readability cap, oldest win
      const text = this.dmgPopupPool.pop() ?? makeText(12, 0xffffff);
      if (text.parent !== this.floorLayer) {
        text.anchor.set(0.5);
        text.zIndex = Layer.Fx;
        this.floorLayer.addChild(text);
      }
      const taken = d.kind === 'taken' || d.kind === 'burn';
      text.text = taken ? `-${d.amount}` : `${d.amount}`;
      text.style.fill = d.kind === 'taken' ? Palette.danger
        : d.kind === 'burn' ? Palette.enemyHotAlt
          : d.kind === 'dot' ? 0xcfd6e6 : 0xffffff;
      text.style.fontSize = d.kind === 'taken' ? 17
        : d.kind === 'dot' ? 11
          : Math.round(12 + Math.min(8, d.amount * 0.1));
      text.style.fontWeight = taken ? 'bold' : 'normal';
      text.position.set(d.x + (Math.random() - 0.5) * 16, d.y - 12);
      text.alpha = 1;
      text.visible = true;
      this.dmgPopups.push({ text, age: 0, max: taken ? 0.9 : 0.7 });
    }
    feed.length = 0;
  }

  private setMinimap(m: Minimap): void {
    if (this.minimap) this.hud.removeChild(this.minimap.root);
    this.minimap = m;
    this.hud.addChild(m.root);
    // Round 5 (Sarah's A/B): the HUD corner map is hidden — the map lives
    // enlarged on the pause screen. Flip HUD_MINIMAP to bring it back.
    m.root.visible = HUD_MINIMAP;
  }

  update(dt: number, input: InputSnapshot): void {
    this.time += dt;
    this.camera.tick(dt);
    if (this.playerFlash > 0) this.playerFlash--;

    // Victory: linger on the gem shower, then the win screen.
    if (this.winDelay > 0 && --this.winDelay === 0) {
      this.endRun(true);
      return;
    }

    // Death: brief pause on the corpse, then the end screen.
    if (!this.player.alive) {
      if (this.deathDelay < 0) {
        this.deathDelay = 55;
        this.camera.addTrauma(0.8);
        this.ctx.loop.hitstop(0.18);
        this.world.spawnSparks(this.player.x, this.player.y, 40, 1.6);
      } else if (--this.deathDelay === 0) {
        this.endRun(false);
        return;
      }
      this.world.particles.update(dt, this.player.x, this.player.y, this.world.bounds);
      return;
    }

    if (input.pocketPressed && usePocket(this.runState, this.player, this.events)) {
      this.pushToast('Mend — restored 2 HP', Palette.gem);
    }

    this.world.update(dt, input);
    this.syncDamagePopups(dt);
    // Cartographer's Sketch: light the minimap once per floor.
    if (this.runState.floorIntel && !this.intelApplied && this.minimap) {
      this.intelApplied = true;
      this.minimap.revealAll();
      this.pushToast('The sketch marks every secret on this floor', Palette.gold);
    }
    this.minimap?.reveal(this.player.x, this.player.y);

    for (let i = this.effectRings.length - 1; i >= 0; i--) {
      const ring = this.effectRings[i]!;
      ring.age += dt;
      if (ring.age >= ring.maxAge) {
        this.effectRings[i] = this.effectRings[this.effectRings.length - 1]!;
        this.effectRings.pop();
      }
    }
    for (let i = this.lightningBolts.length - 1; i >= 0; i--) {
      const bolt = this.lightningBolts[i]!;
      bolt.age += dt;
      if (bolt.age >= bolt.maxAge) {
        this.lightningBolts[i] = this.lightningBolts[this.lightningBolts.length - 1]!;
        this.lightningBolts.pop();
      }
    }
    for (let i = this.toasts.length - 1; i >= 0; i--) {
      const toast = this.toasts[i]!;
      toast.age += dt;
      if (toast.age > 3.5) {
        this.hud.removeChild(toast.text);
        toast.text.destroy();
        this.toasts.splice(i, 1);
      }
    }

    // Queued picks: offer one at a time; multiple bar thresholds queue up.
    // Never after the boss dies — no overlay during the win linger.
    const rs = this.runState;
    if (!this.world.bossDefeated && picksEarned(rs.barFill) > rs.picksConsumed) {
      const offerCtx = () => ({
        pickIndex: rs.picksConsumed - 1, // the pick being offered
        character: rs.character,
        guidedPicks: this.ctx.profile.data.unlocks.guidedPicks,
        banished: rs.banished,
      });
      rs.picksConsumed++;
      const choices = rollOffers(rs.abilities, this.lootRng, offerCtx());
      if (choices.length > 0) {
        this.ctx.scenes.push(new LevelUpOverlay(
          this.ctx,
          choices,
          (choice) => {
            applyOffer(rs.abilities, choice);
          },
          () => {
            const cost = rerollCost(rs.rerollsUsed);
            return { cost, affordable: rs.gemsHeld >= cost };
          },
          () => {
            // Paid reroll: wallet only — gemsEarned and the unbanked stake
            // never move (they're income measures, not spend).
            rs.gemsHeld -= rerollCost(rs.rerollsUsed);
            rs.rerollsUsed++;
            return rollOffers(rs.abilities, this.lootRng, offerCtx());
          },
          () => rs.banishesLeft,
          (choice) => {
            rs.banishesLeft--;
            rs.banished.add(choice.defId);
            this.pushToast(`${ABILITY_DEFS[choice.defId].name} banished from this run`, Palette.hudDim);
            return rollOffers(rs.abilities, this.lootRng, offerCtx());
          },
        ));
        return;
      }
    }

    const ex = this.exitGfx.position.x;
    const ey = this.exitGfx.position.y;
    const nearExit = dist2(this.player.x, this.player.y, ex, ey) < EXIT_RANGE * EXIT_RANGE;
    const nearShop = this.shopGfx !== null &&
      dist2(this.player.x, this.player.y, this.shopGfx.position.x, this.shopGfx.position.y) <
      EXIT_RANGE * EXIT_RANGE;

    if (nearExit && input.interactPressed) {
      // In the vault the "exit" is the return door — NEVER the descend path
      // (no banking, no floor++, no clearFloorBuffs).
      if (this.originStash !== null) {
        this.exitVault();
        return;
      }
      // Summon lock: the circle is a commitment — no fresh-floor boss farm.
      if (this.world.descentLocked) {
        this.pushToast('The summons holds — see it through', Palette.enemyHot);
        return;
      }
      this.descend();
      return;
    }
    if (nearShop && input.interactPressed) {
      this.ctx.scenes.push(new ShopOverlay(this.ctx, this.shopStock, this.runState, this.player));
      return;
    }

    // Cask's Shortcut fuse: the ACTIVE button (MA — hold to channel, not in
    // the vault). Its own button means the old nearExit/nearShop conflicts
    // with interact are gone.
    if (
      rs.character === 'cask' && this.originStash === null &&
      input.activeHeld && this.player.alive
    ) {
      // Summon lock: no fuse-blasting past a standing circle either.
      if (this.world.descentLocked) {
        if (this.fuseChannel === 0) {
          this.pushToast('The summons holds — the floor will not give', Palette.enemyHot);
        }
        this.fuseChannel = 0.0001; // toast once per hold, never channel
        return;
      }
      this.fuseChannel += dt;
      if (this.fuseChannel >= 1.2) {
        this.fuseChannel = 0;
        this.world.selfDamage(1);
        this.pushToast('The floor gives way', Palette.gold);
        this.camera.addTrauma(0.6);
        if (this.player.alive) this.descend();
        return;
      }
    } else {
      this.fuseChannel = 0;
    }

    // Bram & Gaunt (M0): kneel at the body (hold interact 1.5s, costs 1 HP —
    // hence the hp > 1 gate) to revive Gaunt at half HP mid-floor.
    const houndBody = this.world.hound !== null && !this.world.hound.alive
      ? this.world.hound : null;
    const nearBody = houndBody !== null &&
      dist2(this.player.x, this.player.y, houndBody.x, houndBody.y) < 52 * 52;
    if (nearBody && input.interactHeld && this.player.alive && this.player.hp > 1) {
      this.reviveChannel += dt;
      if (this.reviveChannel >= 1.5) {
        this.reviveChannel = 0;
        this.world.reviveHound();
      }
    } else {
      this.reviveChannel = 0;
    }

    // Floor events: first-sight flavor + nearest live event in interact range.
    let nearEvent: { ev: FloorEvent; gfx: Container; seen: boolean; sub: number | undefined } | null = null;
    let bestEd = EXIT_RANGE * EXIT_RANGE;
    for (const rec of this.eventGfx) {
      const d = dist2(this.player.x, this.player.y, rec.gfx.position.x, rec.gfx.position.y);
      if (!rec.seen && d < EVENT_SEEN_RANGE * EVENT_SEEN_RANGE) {
        rec.seen = true;
        this.pushToast(EVENT_SEEN_TOAST[rec.ev.type], EVENT_COLOR[rec.ev.type]);
      }
      if (!eventConsumed(rec.ev) && d < bestEd) {
        bestEd = d;
        nearEvent = rec;
      }
    }
    if (nearEvent !== null && input.interactPressed) {
      const ev = nearEvent.ev;
      switch (ev.type) {
        case 'merchant':
          this.ctx.scenes.push(new ShopOverlay(this.ctx, ev.stock, this.runState, this.player));
          return;
        case 'lootChest':
          this.world.openChest(ev);
          return;
        case 'ambush':
          this.world.triggerAmbush(ev);
          return;
        case 'shrine':
          this.ctx.scenes.push(new ShrineOverlay(this.ctx, ev, () => {
            acceptShrine(ev, this.runState);
            this.events.emit('shrineTaken', { boonId: ev.offer.boonId, curseId: ev.offer.curseId });
          }));
          return;
        case 'vaultDoor':
          this.enterVault(ev);
          return;
        case 'boneKey':
          this.world.takeKey(ev);
          return;
        case 'strongbox':
          this.world.openStrongbox(ev);
          return;
        case 'brazierRelay': {
          const sub = nearEvent.sub ?? 0;
          // The chest sits at the LAST-lit brazier (its pos was moved there
          // at unseal) — reference-compare against the braziers array.
          if (ev.unsealed && ev.braziers[sub] === ev.chest.pos) this.world.openChest(ev.chest);
          else this.world.lightBrazier(ev, sub);
          return;
        }
        case 'spring':
          this.world.useSpring(ev);
          return;
      }
    }

    if (input.pausePressed) {
      this.ctx.scenes.push(new PauseOverlay(
        this.ctx,
        () => this.ctx.scenes.replace(new MenuScene(this.ctx)),
        this.runState.seed,
        this.runState.floor,
        this.runState,
        this.minimap,
        { x: this.player.x, y: this.player.y },
      ));
      return;
    }

    if (input.overviewPressed) {
      this.ctx.scenes.push(new RunOverviewOverlay(this.ctx, this.runState, this.player));
      return;
    }

    const circle = this.world.circle;
    const nearCircle = circle !== null &&
      dist2(this.player.x, this.player.y, circle.x, circle.y) < 52 * 52;

    const altar = this.world.altarPos;
    const nearAltar = altar !== null &&
      dist2(this.player.x, this.player.y, altar.x, altar.y) < 52 * 52;
    const vChest = this.world.vaultChest;
    const nearVaultChest = vChest !== null &&
      dist2(this.player.x, this.player.y, vChest.x, vChest.y) < 52 * 52;
    if (nearVaultChest && input.interactPressed) {
      this.world.openVaultChest();
      if (vChest.opened && this.vaultChestGfx) this.vaultChestGfx.alpha = 0.35;
    }

    const key = input.device === 'pad' ? 'Ⓐ' : '[E]';
    const eventHint: Record<FloorEventType, string> = {
      merchant: `Trade  ${key}`,
      lootChest: `Open chest  ${key}`,
      ambush: `Open chest  ${key}`,
      shrine: `Commune  ${key}`,
      vaultDoor: `Enter the Reliquary  ${key}`,
      boneKey: `Take the bone key  ${key}`,
      // State-dependent hints below override these table entries.
      strongbox: `Unlock the strongbox  ${key}`,
      brazierRelay: `Light the brazier  ${key}`,
      spring: `Drink  ${key}`,
    };
    let nearEventHint = nearEvent !== null ? eventHint[nearEvent.ev.type] : '';
    if (nearEvent?.ev.type === 'strongbox' && !this.world.keyHeld) {
      nearEventHint = 'Locked — its keyhole is bone-shaped';
    } else if (nearEvent?.ev.type === 'brazierRelay') {
      const ev = nearEvent.ev;
      const sub = nearEvent.sub ?? 0;
      if (ev.unsealed) {
        nearEventHint = ev.braziers[sub] === ev.chest.pos
          ? `Open the chest  ${key}` : 'It burns steady';
      } else if (ev.lit[sub]) {
        nearEventHint = `It burns — ${Math.ceil(ev.window)}s for the rest`;
      }
    }
    const altarHint = this.world.altarUsed
      ? 'The altar is spent'
      : !this.world.vaultBossDefeated
        ? 'The altar is silent while its Keeper stands'
        : this.world.hasCleanseTarget()
          ? `Hold ${key} — Cleanse`
          : 'Cleansing Altar — nothing cursed';
    const vaultChestHint = vChest?.opened
      ? 'The chest lies empty'
      : !this.world.vaultBossDefeated
        ? 'The chest is sealed while its Keeper stands'
        : `Open the chest  ${key}`;
    this.hintText.text = nearExit
      ? (this.originStash !== null
        ? `Return  ${key}`
        : this.world.descentLocked ? 'Sealed — the summons holds' : `Descend  ${key}`)
      : nearShop
        ? `Browse  ${key}`
        : nearBody
          ? (this.player.hp > 1 ? `Hold ${key} — Revive Gaunt (−1 HP)` : 'Too weak to revive Gaunt')
          : nearAltar
            ? altarHint
            : nearVaultChest
              ? vaultChestHint
              : nearEvent !== null
                ? nearEventHint
                : nearCircle
                  ? (input.device === 'pad' ? 'Hold Ⓐ — Summon' : 'Hold [E] — Summon')
                  : '';
  }

  render(alpha: number): void {
    const p = this.player;
    const ix = lerp(p.prevX, p.x, alpha);
    const iy = lerp(p.prevY, p.y, alpha);
    const screenW = this.ctx.app.screen.width;
    const screenH = this.ctx.app.screen.height;

    this.camera.follow(ix, iy, screenW, screenH);
    this.playerGfx.position.set(ix, iy);
    this.playerGfx.visible = p.alive;
    this.playerGfx.tint = this.playerFlash > 0
      ? Palette.danger
      : p.curseLeft > 0 ? 0xd8a8ff : 0xffffff;
    this.playerGfx.alpha = p.iframes > 0 ? (Math.floor(p.iframes / 4) % 2 ? 0.35 : 0.9) : 1;
    if (this.exitGfx instanceof ExitPortal) this.exitGfx.animate(this.time);
    else this.exitGfx.scale.set(1 + 0.06 * Math.sin(this.time * 3.2));
    for (let k = 0; k < this.eventGfx.length; k++) {
      const rec = this.eventGfx[k]!;
      if (rec.ev.type === 'brazierRelay' && !eventConsumed(rec.ev)) {
        // Lit braziers swap to the flame sprite and glow full; unlit ones
        // sit dim so progress reads at a glance. Unsealed = all three hold.
        const lit = rec.ev.unsealed || rec.ev.lit[rec.sub ?? 0] === true;
        const litS = rec.gfx.getChildByLabel('lit');
        const unlitS = rec.gfx.getChildByLabel('unlit');
        if (litS) litS.visible = lit;
        if (unlitS) unlitS.visible = !lit;
        rec.gfx.alpha = lit ? 1 : 0.55;
      } else if (eventConsumed(rec.ev)) {
        rec.gfx.alpha = 0.25;
        // Consumed events vanish from the map too (round 5). eventGfx[k] ↔
        // markers[k] is 1:1 — both are the same flatMap over floorEvents.
        this.minimap?.hideMarker(k);
      } else {
        rec.gfx.alpha = 1;
      }
    }
    for (const rec of this.geodeGfx) rec.gfx.alpha = rec.g.broken ? 0.18 : 1;

    this.syncEnemies(alpha);
    for (const r of this.renderers) r.sync(alpha);
    this.telegraphRenderer.sync();
    this.syncEffects(ix, iy);

    // HUD
    // Skip HUD placement while the PauseOverlay has the map borrowed
    // (reparented) — its coordinates belong to the overlay then.
    if (this.minimap && this.minimap.root.parent === this.hud) {
      this.minimap.sync(p.x, p.y);
      this.minimap.root.position.set(screenW - 160, 8);
    }
    // Right-aligned flush with the minimap's right edge — long labels
    // ("floor 7  ·  level 3") grow leftward instead of off-screen.
    this.floorText.position.set(screenW - 10, 170);
    this.seedText.position.set(screenW - 10, 192);
    this.gemText.position.set(14, 124);
    this.gemText.text = `◆ ${Math.round(this.runState.gemsHeld)}`;
    this.hintText.position.set(screenW / 2, screenH / 2 - 50);
    this.syncVignette(screenW, screenH);
    this.syncHearts();
    this.syncAspectWidgets();
    this.syncSlots(screenH);
    this.syncArtifactStrip(screenW, screenH);
    this.syncDrops();
    this.syncSummonAndBoss(screenW);
    this.syncCircle();
    for (let i = 0; i < this.toasts.length; i++) {
      const toast = this.toasts[i]!;
      toast.text.position.set(screenW / 2, 90 + i * 22);
      toast.text.alpha = toast.age < 3 ? 1 : 1 - (toast.age - 3) / 0.5;
    }
  }

  /** Top-center: summon bar normally, boss HP bar during the fight. */
  private syncSummonAndBoss(screenW: number): void {
    const g = this.summonGfx;
    const b = this.bossBarGfx;
    g.clear();
    b.clear();
    this.bumpText.visible = false;
    this.syncTimer(screenW);
    const boss = this.world.boss;
    if (boss && boss.alive) {
      this.barPctText.visible = false;
      this.bossNameText.visible = true;
      this.bossNameText.text = this.world.bossDef?.name ?? '';
      this.bossNameText.position.set(screenW / 2, 10);
      const w = 420;
      const x = (screenW - w) / 2;
      const frac = Math.max(0, boss.hp / boss.maxHp);
      b.roundRect(x - 2, 30, w + 4, 16, 4).fill({ color: 0x000000, alpha: 0.55 });
      b.roundRect(x, 32, w * frac, 12, 3).fill(Palette.enemyHot);
      b.roundRect(x - 2, 30, w + 4, 16, 4).stroke({ width: 1.5, color: Palette.enemyHot, alpha: 0.6 });
      return;
    }
    // The Keeper gets the same bar treatment in gold (never world.boss —
    // that field is wired to the run-win path).
    const keeper = this.world.keeper;
    if (keeper && keeper.alive) {
      this.barPctText.visible = false;
      this.bossNameText.visible = true;
      this.bossNameText.text = 'THE KEEPER';
      this.bossNameText.position.set(screenW / 2, 10);
      const w = 340;
      const x = (screenW - w) / 2;
      const frac = Math.max(0, keeper.hp / keeper.maxHp);
      b.roundRect(x - 2, 30, w + 4, 16, 4).fill({ color: 0x000000, alpha: 0.55 });
      b.roundRect(x, 32, w * frac, 12, 3).fill(Palette.gold);
      b.roundRect(x - 2, 30, w + 4, 16, 4).stroke({ width: 1.5, color: Palette.gold, alpha: 0.6 });
      return;
    }
    this.bossNameText.visible = false;
    if (this.world.bossDefeated) {
      this.barPctText.visible = false;
      return;
    }
    const frac = summonBarFill(this.runState.barFill);
    const w = Math.min(520, screenW * 0.45);
    const x = (screenW - w) / 2;
    const full = frac >= 1;
    const pulse = full ? 0.75 + 0.25 * Math.sin(this.time * 6) : 1;
    g.roundRect(x - 2, 12, w + 4, 26, 6).fill({ color: 0x000000, alpha: 0.5 });
    g.roundRect(x, 15, w * frac, 20, 5).fill({
      color: full ? Palette.enemyHot : 0x8a4a9e,
      alpha: pulse,
    });
    // % readout (Sarah, 2026-07-27): the fill is now a strategic clock —
    // give it a number. Reads SUMMON at full, riding the same pulse.
    this.barPctText.visible = true;
    this.barPctText.position.set(screenW / 2, 25);
    this.barPctText.text = full ? 'SUMMON' : `${Math.floor(frac * 100)}%`;
    this.barPctText.alpha = full ? pulse : 0.95;
    // Pick notches: the bar IS the pick track — 5 threshold ticks, dimmed
    // once their pick has been consumed.
    for (let i = 0; i < PICK_FRACTIONS.length; i++) {
      const nx = x + w * PICK_FRACTIONS[i]!;
      const taken = i < this.runState.picksConsumed;
      g.rect(nx - 1, 15, 2, 20).fill({
        color: taken ? 0x3a2a44 : 0xd8c8e8,
        alpha: taken ? 0.6 : 0.9,
      });
    }
    g.roundRect(x - 2, 12, w + 4, 26, 6).stroke({ width: 1.5, color: 0x8a4a9e, alpha: 0.7 });
    // Secondary bar: gem progress toward the NEXT pickup-track stat bump,
    // with the current bump count alongside.
    const rs = this.runState;
    const eff = rs.gemsEarned * rs.stats.resolve('pickupRateMult');
    const n = rs.pickupBumpsGranted;
    const atCap = n >= PICKUP_BUMP_CAP;
    const lo = n > 0 ? bumpThreshold(n) : 0;
    const hi = bumpThreshold(n + 1);
    const gemFrac = atCap ? 1 : Math.max(0, Math.min(1, (eff - lo) / (hi - lo)));
    g.roundRect(x - 2, 41, w + 4, 9, 3).fill({ color: 0x000000, alpha: 0.45 });
    g.roundRect(x, 43, w * gemFrac, 5, 2).fill({
      color: Palette.gem, alpha: atCap ? 0.45 : 0.9,
    });
    g.roundRect(x - 2, 41, w + 4, 9, 3).stroke({ width: 1, color: Palette.gem, alpha: 0.35 });
    this.bumpText.text = atCap ? `⬦ ${n} MAX` : `⬦ ${n}`;
    this.bumpText.position.set(x + w + 12, 46);
    this.bumpText.visible = true;
  }

  /**
   * Run-timer countdown just under the summon bar; hidden while a boss or
   * Keeper bar owns that band (by then enrage spawning has stopped anyway).
   * Last 2 min: hot + pulsing. Overtime: "+M:SS" count-up.
   */
  private syncTimer(screenW: number): void {
    const t = this.timerText;
    // The clock never pauses — so it never hides either (Sarah, 2026-07-27:
    // overtime pressure must be visible DURING boss duels). y54 sits below
    // the boss bar's 30–46 band. Only the post-win linger hides it.
    if (this.world.bossDefeated) {
      t.visible = false;
      return;
    }
    t.visible = true;
    t.position.set(screenW / 2, 54);
    const remaining = RUN_TIME_LIMIT_SECONDS - this.runState.timeSeconds;
    if (remaining >= 0) {
      t.text = formatRunTime(remaining);
      const urgent = remaining < 120;
      t.style.fill = urgent ? Palette.enemyHot : Palette.hudText;
      t.alpha = urgent ? 0.7 + 0.3 * Math.sin(this.time * 6) : 0.9;
    } else {
      t.text = `+${formatRunTime(-remaining)}`;
      t.style.fill = Palette.enemyHot;
      t.alpha = 0.7 + 0.3 * Math.sin(this.time * 6);
    }
  }

  /** The summoning circle: rotating runes + channel progress arc. */
  private syncCircle(): void {
    const g = this.circleGfx;
    g.clear();
    const circle = this.world.circle;
    if (!circle) return;
    const t = this.time;
    g.circle(circle.x, circle.y, 48).fill({ color: Palette.enemyHot, alpha: 0.07 });
    g.circle(circle.x, circle.y, 46).stroke({ width: 2, color: Palette.enemyHot, alpha: 0.8 });
    g.circle(circle.x, circle.y, 32).stroke({ width: 1.5, color: Palette.enemyHot, alpha: 0.5 });
    for (let i = 0; i < 5; i++) {
      const a = t * 0.8 + (i * Math.PI * 2) / 5;
      const rx = circle.x + Math.cos(a) * 39;
      const ry = circle.y + Math.sin(a) * 39;
      g.poly([rx, ry - 5, rx + 5, ry, rx, ry + 5, rx - 5, ry]).fill({
        color: Palette.enemyHot, alpha: 0.85,
      });
    }
    const channel = this.world.channel;
    if (channel > 0) {
      g.arc(circle.x, circle.y, 56, -Math.PI / 2, -Math.PI / 2 + (channel / 1.5) * Math.PI * 2)
        .stroke({ width: 4, color: Palette.gold });
    }
  }

  private pushToast(msg: string, color: number): void {
    const text = new Text({
      text: msg,
      style: { fontFamily: 'monospace', fontSize: 14, fill: color },
    });
    text.anchor.set(0.5, 0);
    this.hud.addChild(text);
    this.toasts.push({ text, age: 0 });
  }

  /**
   * Ground artifact drops: ONE uniform icon for every drop — quality, proc,
   * and curse are never identified until the artifact is in your hands.
   */
  private syncDrops(): void {
    const g = this.dropsGfx;
    g.clear();
    for (const drop of this.world.artifactDrops) {
      const color = Palette.gold;
      const bob = Math.sin(this.time * 4 + drop.x) * 3;
      const y = drop.y + bob;
      g.circle(drop.x, y, 16).fill({ color, alpha: 0.12 });
      g.circle(drop.x, y, 13 + Math.sin(this.time * 5) * 1.5)
        .stroke({ width: 2, color, alpha: 0.85 });
      g.poly([drop.x, y - 8, drop.x + 8, y, drop.x, y + 8, drop.x - 8, y]).fill(color);
      g.poly([drop.x, y - 4, drop.x + 4, y, drop.x, y + 4, drop.x - 4, y]).fill(0xffffff);
    }
    // Ground zones (Caustic pools, Sanctum, burning trail): soft player-lane
    // discs that fade out over their last second. 'gas' (the one HOSTILE
    // zone kind) renders toxic yellow-green — neither gem green nor player
    // cyan — and denser, so it reads as "do not stand here".
    for (const zone of this.world.zones) {
      const hostile = zone.kind === 'gas';
      const c = hostile ? 0xb4cc2e : zone.kind === 'burn' ? Palette.gold : Palette.playerCoolSoft;
      const fade = Math.min(1, zone.duration);
      g.circle(zone.x, zone.y, zone.radius).fill({ color: c, alpha: (hostile ? 0.18 : 0.10) * fade });
      g.circle(zone.x, zone.y, zone.radius * (0.94 + 0.04 * Math.sin(this.time * 4 + zone.x)))
        .stroke({ width: hostile ? 2 : 1.5, color: c, alpha: (hostile ? 0.6 : 0.4) * fade });
      if (hostile) {
        // Drifting motes sell the fume without a particle burst.
        for (let m = 0; m < 3; m++) {
          const a = this.time * 0.9 + m * 2.1 + zone.x * 0.01;
          g.circle(
            zone.x + Math.cos(a) * zone.radius * 0.5,
            zone.y + Math.sin(a * 1.3) * zone.radius * 0.45,
            3,
          ).fill({ color: c, alpha: 0.4 * fade });
        }
      }
    }
    // Mourne's crawlers: small allied wedges (player-lane, never enemy-read).
    // Whipped to fury (Exhume), they run gold-hot.
    const crawlerColor = this.world.crawlerEnrage > 0 ? Palette.gold : Palette.playerCool;
    for (const cr of this.world.crawlers) {
      const wob = Math.sin(this.time * 9 + cr.x) * 2;
      g.poly([cr.x, cr.y - 6 + wob, cr.x + 5, cr.y + 4 + wob, cr.x - 5, cr.y + 4 + wob])
        .fill({ color: crawlerColor, alpha: 0.9 });
    }
    // Gaunt: a bounding gold diamond with a health sliver; downed, a dim
    // husk of the same diamond marks the body (the revive target).
    const hound = this.world.hound;
    if (hound && hound.alive) {
      g.poly([hound.x, hound.y - 12, hound.x + 10, hound.y, hound.x, hound.y + 12, hound.x - 10, hound.y])
        .fill({ color: Palette.gold, alpha: 0.95 });
      g.circle(hound.x, hound.y, 16).stroke({ width: 1.5, color: Palette.gold, alpha: 0.4 });
      const frac = Math.max(0, hound.hp / hound.maxHp);
      g.rect(hound.x - 12, hound.y - 20, 24 * frac, 3).fill(Palette.gold);
    } else if (hound) {
      const pulse = 0.35 + 0.25 * Math.sin(this.time * 4);
      g.poly([hound.x, hound.y - 12, hound.x + 10, hound.y, hound.x, hound.y + 12, hound.x - 10, hound.y])
        .stroke({ width: 2, color: Palette.hudDim, alpha: 0.8 });
      g.circle(hound.x, hound.y, 20).stroke({ width: 1.5, color: Palette.enemyHot, alpha: pulse });
    }
    // Cask's fuse: a closing ring while the channel holds.
    if (this.fuseChannel > 0) {
      const t = Math.min(1, this.fuseChannel / 1.2);
      g.circle(this.player.x, this.player.y, 40 * (1 - t) + 12)
        .stroke({ width: 3, color: Palette.gold, alpha: 0.8 });
    }
    // Bram's revive kneel: same closing-ring language at the body.
    if (this.reviveChannel > 0 && hound && !hound.alive) {
      const t = Math.min(1, this.reviveChannel / 1.5);
      g.circle(hound.x, hound.y, 40 * (1 - t) + 12)
        .stroke({ width: 3, color: Palette.playerCool, alpha: 0.8 });
    }
  }

  /** Bottom-right strip of equipped artifacts; brought (SAFE) get a pip. */
  private syncArtifactStrip(screenW: number, screenH: number): void {
    const key = this.runState.artifacts.map((a) => `${a.artifactId}:${a.quality}:${a.proc}`).join(',');
    if (key !== this.artifactStripKey) {
      this.artifactStripKey = key;
      this.artifactStrip.removeChildren().forEach((c) => c.destroy());
      this.runState.artifacts.forEach((artifact, i) => {
        const def = ARTIFACTS_BY_ID.get(artifact.artifactId);
        if (!def) return;
        const color = PROC_COLOR[artifact.proc];
        const g = new Graphics();
        const x = i * 26;
        g.poly([x + 10, 0, x + 20, 10, x + 10, 20, x, 10]).fill({ color, alpha: 0.9 });
        g.poly([x + 10, 5, x + 15, 10, x + 10, 15, x + 5, 10]).fill(0xffffff);
        if (this.runState.broughtIds.has(artifact.artifactId)) {
          // Underline pip: this one is safe.
          g.rect(x + 4, 23, 12, 2.5).fill(Palette.playerCool);
        }
        this.artifactStrip.addChild(g);
      });
    }
    this.artifactStrip.position.set(
      screenW - 14 - this.runState.artifacts.length * 26,
      screenH - 40,
    );
  }

  /** Ability visuals: expanding pulse rings + orbiting wards + arena walls. */
  private syncEffects(px: number, py: number): void {
    const g = this.effectGfx;
    g.clear();
    const arena = this.world.arena;
    if (arena) {
      const pulse = 0.35 + 0.15 * Math.sin(this.time * 5);
      g.rect(arena.minX, arena.minY, arena.maxX - arena.minX, arena.maxY - arena.minY)
        .stroke({ width: 4, color: Palette.enemyHot, alpha: pulse });
    }
    for (const ring of this.effectRings) {
      const t = ring.age / ring.maxAge;
      g.circle(ring.x, ring.y, ring.radius * (0.35 + 0.65 * t))
        .stroke({ width: 3 * (1 - t) + 1, color: ring.color, alpha: 0.6 * (1 - t) });
    }

    const w = this.wardGfx;
    w.clear();
    const areaMult = this.runState.stats.resolve('areaMult');
    for (const inst of this.runState.abilities) {
      const arch = ABILITY_DEFS[inst.defId].archetype;
      if (arch === 'orbit') {
        for (const [wx, wy] of AbilitySystem.orbitPositions(inst, px, py, areaMult)) {
          w.circle(wx, wy, 15).fill({ color: Palette.playerCool, alpha: 0.14 });
          w.circle(wx, wy, 10).stroke({ width: 2, color: Palette.playerCool, alpha: 0.9 });
          w.circle(wx, wy, 4).fill(Palette.playerCoolSoft);
        }
      } else if (arch === 'censer') {
        // Wick's smoldering aura: a breathing ring at the TRUE damage
        // radius, filled faintly, with little embers drifting inside it —
        // pure render math on this.time/inst.angle, no sim state.
        const p = skillParams(inst);
        const auraR = p.radius * areaMult;
        const breathe = 1 + 0.025 * Math.sin(this.time * 2.6);
        w.circle(px, py, auraR * breathe).fill({ color: Palette.gold, alpha: 0.06 });
        w.circle(px, py, auraR * breathe)
          .stroke({ width: 2, color: Palette.gold, alpha: 0.35 + 0.08 * Math.sin(this.time * 2.6) });
        w.circle(px, py, auraR * 0.55).stroke({ width: 1.5, color: Palette.enemyHotAlt, alpha: 0.12 });
        for (let m = 0; m < 6; m++) {
          // Each ember rides its own slow orbit while bobbing upward and
          // guttering in and out; inst.angle keeps them idly swirling.
          const a = inst.angle * 0.7 + (m * TAU) / 6 + Math.sin(this.time * 1.3 + m * 2.4) * 0.5;
          const r = auraR * (0.35 + 0.45 * (0.5 + 0.5 * Math.sin(this.time * 0.9 + m * 1.7)));
          const bob = Math.sin(this.time * 2.2 + m * 1.9) * 5 - 3;
          const flicker = 0.35 + 0.4 * (0.5 + 0.5 * Math.sin(this.time * 5.1 + m * 3.3));
          w.circle(px + Math.cos(a) * r, py + Math.sin(a) * r + bob, 1.8 + (m % 3) * 0.7)
            .fill({ color: m % 2 ? Palette.gold : Palette.enemyHotAlt, alpha: flicker });
        }
      }
    }
    // Trine's split form: two ghost selves while Sundered.
    if (this.world.sunderLeft > 0) {
      for (const off of [-34, 34]) {
        w.circle(px + off, py - 6, 10).stroke({ width: 2, color: Palette.playerCoolSoft, alpha: 0.7 });
      }
    }
    // Barrow's Ironbound aura (2026-07-28 clarity pass): the TRUE damage
    // radius — fixed 110, deliberately NOT areaMult-scaled (the sim aura
    // isn't either; the censer-ring lesson). Ramps with stacks, pulses on
    // the 0.5s damage tick.
    if (this.runState.character === 'barrow' && this.world.ironboundStacks > 0) {
      const s = this.world.ironboundStacks;
      const pulse = Math.max(0, this.world.ironboundTick) / 0.5;
      w.circle(px, py, IRONBOUND_AURA_RADIUS).fill({ color: Palette.enemyHotAlt, alpha: 0.015 * s });
      w.circle(px, py, IRONBOUND_AURA_RADIUS).stroke({
        width: 1.5 + 0.3 * s, color: Palette.enemyHotAlt,
        alpha: 0.12 + 0.06 * s + 0.12 * (1 - pulse),
      });
    }
    // Chain lightning: jittered polyline through the chain points. Thirl's
    // leech keeps the hot orange whip; Witchlight arcs (round 5) are
    // thicker and FORKY — two jittered midpoints, a stray branch per
    // segment, and a soft under-glow. Pure render math on this.time.
    for (const bolt of this.lightningBolts) {
      const t = bolt.age / bolt.maxAge;
      const alpha = 0.9 * (1 - t);
      if (bolt.kind === 'arc') {
        const width = 3.5 * (1 - t) + 1;
        for (let s = 0; s < bolt.points.length - 1; s++) {
          const p0 = bolt.points[s]!;
          const p1 = bolt.points[s + 1]!;
          const dx = p1.x - p0.x;
          const dy = p1.y - p0.y;
          const len = Math.hypot(dx, dy) || 1;
          const nx = -dy / len; // perpendicular
          const ny = dx / len;
          const j1 = 7 * Math.sin(this.time * 47 + s * 13);
          const j2 = -7 * Math.sin(this.time * 39 + s * 5 + 2);
          const a = { x: p0.x + dx / 3 + nx * j1, y: p0.y + dy / 3 + ny * j1 };
          const b = { x: p0.x + (2 * dx) / 3 + nx * j2, y: p0.y + (2 * dy) / 3 + ny * j2 };
          const path = () => {
            w.moveTo(p0.x, p0.y).lineTo(a.x, a.y).lineTo(b.x, b.y).lineTo(p1.x, p1.y);
          };
          path();
          w.stroke({ width: width * 2.2, color: Palette.playerCoolSoft, alpha: alpha * 0.25 });
          path();
          w.stroke({ width, color: Palette.playerCoolSoft, alpha });
          // A stray branch off the first midpoint, flickering side to side.
          const side = Math.sin(this.time * 31 + s * 17) > 0 ? 1 : -1;
          const fx = a.x + (dx / len) * 9 + nx * side * 12;
          const fy = a.y + (dy / len) * 9 + ny * side * 12;
          w.moveTo(a.x, a.y).lineTo(fx, fy)
            .stroke({ width: width * 0.5, color: Palette.playerCoolSoft, alpha: alpha * 0.6 });
        }
      } else {
        for (let s = 0; s < bolt.points.length - 1; s++) {
          const p0 = bolt.points[s]!;
          const p1 = bolt.points[s + 1]!;
          const jit = 5 * Math.sin(this.time * 40 + s * 7);
          w.moveTo(p0.x, p0.y);
          w.lineTo((p0.x + p1.x) / 2 + jit, (p0.y + p1.y) / 2 - jit);
          w.lineTo(p1.x, p1.y);
          w.stroke({ width: 2.5 * (1 - t) + 0.5, color: Palette.enemyHotAlt, alpha });
        }
      }
    }
  }

  /**
   * Enter the floor's vault side-area ("Reliquary"): STASH the origin world
   * (sim state freezes exactly — the director only advances inside tick) and
   * swap in the cached-or-new vault world. No banking, no floor change, no
   * clearFloorBuffs, no floorEntered.
   */
  private enterVault(ev: FloorEvent): void {
    if (this.originStash !== null) return;
    logJournal(`run: enter vault (floor ${this.runState.floor})`);
    const rs = this.runState;
    this.originStash = {
      world: this.world,
      minimap: this.minimap!,
      shopStock: this.shopStock,
      returnX: TileMap.toWorld(ev.pos.x),
      returnY: TileMap.toWorld(ev.pos.y),
    };

    if (this.vaultWorld === null) {
      const vl = DebugFlags.vaultLayout;
      const vmap = generateVaultMap(
        rs.streams.stream('vaultMap', rs.floor),
        vl !== null && (VAULT_LAYOUTS as readonly string[]).includes(vl)
          ? vl as VaultLayout
          : undefined,
      );
      const vtm = new TileMap(vmap);
      this.vaultWorld = new World(vmap, vtm, rs.floor, this.player, rs, this.events, { vault: true });
      this.vaultMinimap = new Minimap(vmap, false);
    }
    const vworld = this.vaultWorld;
    const vmap = vworld.map;
    this.player.place(TileMap.toWorld(vmap.entrance.x), TileMap.toWorld(vmap.entrance.y));
    this.player.moveResolver = (px, py, nx, ny, r) => vworld.tileMap.resolveMove(px, py, nx, ny, r);
    this.moveCrawlers(this.world, vworld); // this.world is still the origin here
    this.world = vworld;
    this.artifactSystem.setWorld(vworld);
    this.shopStock = [];
    this.buildFloorView({ vault: true });
    this.setMinimap(this.vaultMinimap!);

    this.events.emit('vaultEntered', { floor: rs.floor });
    this.floorText.text = `floor ${rs.floor} — reliquary${rs.tier > 0 ? `  ·  ${tierLabel(rs.tier).toLowerCase()}` : ''}`;
    this.pushToast(
      this.world.vaultBossDefeated
        ? 'The Reliquary lies quiet'
        : 'The Reliquary — something guards its treasure',
      Palette.gold,
    );
  }

  /** Walk back out: restore the stashed origin floor exactly as it was left. */
  private exitVault(): void {
    const stash = this.originStash;
    if (!stash) return;
    logJournal(`run: exit vault (floor ${this.runState.floor})`);
    this.originStash = null;

    this.world = stash.world;
    this.shopStock = stash.shopStock;
    this.player.place(stash.returnX, stash.returnY);
    this.moveCrawlers(this.vaultWorld!, stash.world);
    const world = this.world;
    this.player.moveResolver = (px, py, nx, ny, r) => world.tileMap.resolveMove(px, py, nx, ny, r);
    this.artifactSystem.setWorld(world);
    this.buildFloorView({ vault: false });
    this.setMinimap(stash.minimap); // removed, never destroyed — fog preserved

    this.events.emit('vaultExited', { floor: this.runState.floor });
    this.floorText.text = `floor ${this.runState.floor}` +
      (this.runState.tier > 0 ? `  ·  ${tierLabel(this.runState.tier).toLowerCase()}` : '');
  }

  /**
   * Mourne's crawlers follow him through the vault door (both directions):
   * live crawlers transfer between the stashed worlds, repositioned in a
   * small fan at his feet. Purely positional — TTL/damage ride along.
   */
  private moveCrawlers(from: World, to: World): void {
    if (from.crawlers.length === 0) return;
    from.crawlers.forEach((cr, i) => {
      const a = (i * Math.PI * 2) / from.crawlers.length;
      cr.x = this.player.x + Math.cos(a) * 26;
      cr.y = this.player.y + Math.sin(a) * 26;
      to.crawlers.push(cr);
    });
    from.crawlers.length = 0;
  }

  /** Descent checkpoint: settle this floor's finds into the profile. */
  private settleDescent(): void {
    const rs = this.runState;
    const found = rs.foundArtifacts.map((a) => ({ artifactId: a.artifactId, quality: a.quality }));
    if (found.length === 0) return;
    const s = applyDescentBank(this.ctx.profile, { foundArtifacts: found });
    rs.markBanked(found);
    mergeBankSummary(this.runBanked, s);
    const banked = [
      ...s.bankedNew.map((b) =>
        `${ARTIFACTS_BY_ID.get(b.artifactId)?.name ?? b.artifactId} Q ${Math.round(b.quality * 100)}%`),
      ...s.bankedImproved.map((b) =>
        `${ARTIFACTS_BY_ID.get(b.artifactId)?.name ?? b.artifactId} ${Math.round(b.from * 100)}%→${Math.round(b.to * 100)}%`),
    ];
    if (banked.length > 0) this.pushToast(`⇩ banked  ${banked.join(',  ')}`, Palette.gem);
    if (s.shardsGained > 0) this.pushToast(`+◇ ${s.shardsGained} shards`, Palette.playerCool);
  }

  private endRun(win: boolean): void {
    const rs = this.runState;
    logJournal(`run: ${win ? 'WIN' : 'death'} floor ${rs.floor}, ${Math.round(rs.timeSeconds)}s`);
    const settlement = applyRunEnd(this.ctx.profile, {
      win,
      floor: rs.floor,
      foundArtifacts: rs.foundArtifacts.map((a) => ({
        artifactId: a.artifactId,
        quality: a.quality,
      })),
      // God-mode runs must not tithe gems into the real profile.
      gemsEarned: rs.god ? 0 : Math.round(rs.gemsEarned),
      kills: rs.god ? 0 : rs.kills,
      character: rs.character,
      tier: rs.tier,
      // Sim-authoritative run clock (this.time is animation-only: it also
      // counts win/death linger ticks, so records would read slightly long).
      timeSeconds: rs.timeSeconds,
      recordEligible: this.recordEligible,
    });
    // Display merge: earlier checkpoints first, final settlement last. The
    // profile was already updated at each descent; this only shapes the
    // EndScene summary so both win and death screens show run-wide banking.
    mergeBankSummary(this.runBanked, settlement);
    const summary: RunEndSummary = { ...settlement, ...this.runBanked };
    this.ctx.scenes.replace(new EndScene(this.ctx, {
      win,
      floor: rs.floor,
      tier: rs.tier,
      seed: rs.seed,
      gemsEarned: Math.round(rs.gemsEarned),
      timeSeconds: rs.timeSeconds,
      summary,
    }));
  }

  /** HUD skill slots: letter + upgrade star, bottom-left; pocket box last. */
  private syncSlots(screenH: number): void {
    const key = this.runState.abilities.map((a) => `${a.defId}:${a.upgraded ? 1 : 0}`).join(',') +
      `|pocket:${this.runState.pocket ?? 'none'}`;
    if (key === this.slotsKey) {
      this.slotsGfx.position.set(14, screenH - 64);
      return;
    }
    this.slotsKey = key;
    this.slotsGfx.removeChildren().forEach((c) => c.destroy());
    for (let i = 0; i < ABILITY_SLOTS; i++) {
      const inst = this.runState.abilities[i];
      const box = new Graphics();
      box.roundRect(i * 46, 0, 40, 40, 6)
        .fill({ color: Palette.hudPanel, alpha: 0.85 })
        .stroke({ width: 1.5, color: inst ? Palette.playerCool : Palette.hudDim, alpha: inst ? 0.9 : 0.35 });
      if (inst) {
        const def = ABILITY_DEFS[inst.defId];
        // Upgraded skills carry a gold underline (the one behavioral upgrade).
        if (inst.upgraded) {
          box.rect(i * 46 + 6, 34, 28, 3).fill(Palette.gold);
        }
        const letter = new Text({
          text: def.short,
          style: { fontFamily: 'monospace', fontSize: 18, fontWeight: 'bold', fill: Palette.hudText },
        });
        letter.anchor.set(0.5);
        letter.position.set(i * 46 + 20, 18);
        this.slotsGfx.addChild(box, letter);
      } else {
        this.slotsGfx.addChild(box);
      }
    }
    // Pocket consumable box (Q / Ⓨ), to the right of the skill slots.
    const px = ABILITY_SLOTS * 46 + 12;
    const hasPocket = this.runState.pocket !== null;
    const pocket = new Graphics();
    pocket.roundRect(px, 0, 40, 40, 6)
      .fill({ color: Palette.hudPanel, alpha: 0.85 })
      .stroke({ width: 1.5, color: hasPocket ? Palette.gem : Palette.hudDim, alpha: hasPocket ? 0.9 : 0.3 });
    if (hasPocket) {
      pocket.poly([px + 20, 12, px + 27, 18, px + 20, 30, px + 13, 18]).fill({
        color: Palette.enemyHot, alpha: 0.9,
      });
    }
    const qLabel = new Text({
      text: 'Q',
      style: { fontFamily: 'monospace', fontSize: 9, fill: hasPocket ? Palette.hudText : Palette.hudDim },
    });
    qLabel.position.set(px + 3, 2);
    this.slotsGfx.addChild(pocket, qLabel);
    this.slotsGfx.position.set(14, screenH - 64);
  }

  private syncEnemies(alpha: number): void {
    const live = new Set<number>();
    for (const e of this.world.enemies) {
      live.add(e.id);
      let g = this.enemyGfx.get(e.id);
      if (!g) {
        g = drawEnemy(e.type, e.type === 'boss' ? this.world.bossDef?.id : undefined);
        if (e.affix) {
          // Elite marker: affix-colored ring under the body.
          const ring = new Graphics();
          ring.circle(0, 0, e.radius / 1.35 + 9)
            .stroke({ width: 2.5, color: AFFIX_COLOR[e.affix], alpha: 0.9 });
          g.addChildAt(ring, 0);
        }
        this.enemyGfx.set(e.id, g);
        this.enemyLayer.addChild(g);
      }
      g.position.set(lerp(e.prevX, e.x, alpha), lerp(e.prevY, e.y, alpha));
      // Debuff tint ladder (M0.5 — chill/brand/shred had NO visual, which
      // read as "Rime does nothing"). Priority: chill > brand > wither >
      // shield > shred ramp (deepens with stacks; blinks at full stacks so
      // Shatterpoint's threshold is readable).
      g.tint = e.chillLeft > 0 ? 0xa8d8ff
        : e.brandLeft > 0 ? 0xffc07a
          : e.witherLeft > 0 ? 0x9fe8b8
            : e.shieldHp > 0 ? 0xb8c8ff
              : e.shredStacks >= SHRED_MAX_STACKS
                ? (Math.floor(this.time * 8) % 2 ? 0xff6d98 : 0xffc0d0)
                : e.shredStacks >= 4 ? 0xffa8bd
                  : e.shredStacks > 0 ? 0xffd4de
                    : 0xffffff;
      const eliteScale = e.affix ? 1.35 : 1;
      g.scale.set(eliteScale * (e.flash > 0 ? 1.15 : 1));
      g.alpha = e.flash > 0 ? 1 : 0.92;
      if ((e.type === 'husk' || e.type === 'flanker') && e.state === 'windup') {
        g.scale.set(eliteScale * 0.88);
      }
    }
    for (const [id, g] of this.enemyGfx) {
      if (!live.has(id)) {
        this.enemyLayer.removeChild(g);
        g.destroy();
        this.enemyGfx.delete(id);
      }
    }
  }

  private syncHearts(): void {
    const rs = this.runState;
    const candle = rs.character === 'wick' ? rs.candle : -1;
    const burned = Math.round(rs.burnedHp);
    // Guttered candle flashes — fold the flash phase into the redraw key.
    const flash = candle === 0 ? Math.floor(this.time * 6) % 2 : 0;
    const key = this.player.hp + candle * 1000 + flash * 1e6 + burned * 1e8 +
      rs.wardCharges * 1e10;
    if (key === this.lastHp) return;
    this.lastHp = key;
    const g = this.heartsGfx;
    g.clear();
    // Cask's scorched sockets render after the live row: max HP he'd have
    // back if he descended — the burn is a debt, not a deletion.
    for (let i = 0; i < this.player.maxHp + burned; i++) {
      const x = 14 + i * 22;
      const heart = [x, 92, x + 8, 86, x + 16, 92, x + 8, 104];
      if (i >= this.player.maxHp) {
        g.poly(heart).fill({ color: 0x1a1210, alpha: 0.9 });
        g.poly(heart).stroke({ width: 1, color: Palette.enemyHotAlt, alpha: 0.55 });
        continue;
      }
      const filled = i < this.player.hp;
      g.poly(heart).fill({
        color: filled ? Palette.enemyHot : 0x2a2a35,
        alpha: filled ? 1 : 0.8,
      });
    }
    // Wardstone pips (shop): cool-lane diamonds after the hearts row.
    for (let w = 0; w < rs.wardCharges; w++) {
      const x = 14 + (this.player.maxHp + burned) * 22 + 6 + w * 16;
      g.poly([x + 6, 88, x + 12, 95, x + 6, 102, x, 95]).fill({ color: Palette.playerCool, alpha: 0.9 });
      g.poly([x + 6, 88, x + 12, 95, x + 6, 102, x, 95]).stroke({ width: 1.5, color: Palette.playerCoolSoft, alpha: 0.8 });
    }
    // Wick's candle: color-staged bar under the hearts, burning right-to-left.
    // Deliberately loud — the thin 4px strip went unnoticed in the feel pass
    // and the guttering chip must never feel like an ambush.
    if (candle >= 0) {
      const w = Math.max(132, this.player.maxHp * 22);
      const color = candle > 0.5 ? Palette.gold
        : candle > 0.25 ? Palette.enemyHotAlt
          : Palette.enemyHot;
      g.roundRect(13, 108, w + 2, 12, 3).fill({ color: 0x000000, alpha: 0.55 });
      if (candle > 0) {
        g.roundRect(14, 109, w * candle, 10, 3).fill({ color });
        // Flame nub at the burn edge.
        g.circle(14 + w * candle, 114, 3.5).fill({ color: 0xffffff, alpha: 0.9 });
      }
      g.roundRect(13, 108, w + 2, 12, 3).stroke({
        width: 1.5, color, alpha: candle === 0 ? (flash ? 1 : 0.25) : 0.6,
      });
    }
  }

  /**
   * The status row under the gem counter: the ACTIVE pip (every character —
   * MA) at its head, aspect-specific widgets beside it (Vane's refusal
   * diamond, Gaunt's health bar). Trine needs no extra widget: her Sunder
   * dial IS the active pip (charging arc → gold-pulse ready → cyan draining
   * window). Redrawn every frame — a few primitives.
   */
  private syncAspectWidgets(): void {
    const g = this.aspectGfx;
    g.clear();
    const rs = this.runState;
    const y = 150;
    // Active pip: hollow ring + filling arc while charging; gold pulse when
    // the press would do something; dim when resource-gated.
    const st = activeStatus(this.world);
    const cx = 23;
    const cy = y + 9;
    g.circle(cx, cy, 9).stroke({ width: 1.5, color: Palette.hudDim, alpha: 0.7 });
    if (st.window) {
      // Trine mid-window: what's left of the split.
      g.moveTo(cx, cy)
        .arc(cx, cy, 8, -Math.PI / 2, -Math.PI / 2 + st.frac * TAU)
        .fill({ color: Palette.playerCool, alpha: 0.9 });
    } else if (st.ready) {
      const pulse = 0.7 + 0.3 * Math.sin(this.time * 6);
      g.circle(cx, cy, 7).fill({ color: Palette.gold, alpha: pulse });
    } else {
      g.moveTo(cx, cy)
        .arc(cx, cy, 8, -Math.PI / 2, -Math.PI / 2 + Math.max(0.02, st.frac) * TAU)
        .fill({ color: Palette.hudDim, alpha: 0.55 });
    }

    const x = 40; // aspect widgets sit beside the pip
    if (rs.character === 'vane') {
      // Diamond pip: gold = a death can be refused; hollow = spent.
      const ready = rs.reviveReady;
      const path = [x + 8, y, x + 16, y + 8, x + 8, y + 16, x, y + 8];
      if (ready) g.poly(path).fill({ color: Palette.gold });
      else g.poly(path).stroke({ width: 1.5, color: Palette.hudDim, alpha: 0.9 });
    } else if (rs.character === 'bramGaunt') {
      const hound = this.world.hound;
      if (hound && hound.alive) {
        // Gaunt's health as a small bar next to a paw dot.
        const frac = Math.max(0, hound.hp / HOUND_HP);
        g.circle(x + 5, y + 8, 4.5).fill({ color: Palette.playerCool });
        g.roundRect(x + 14, y + 5, 60, 6, 2).fill({ color: 0x000000, alpha: 0.55 });
        g.roundRect(x + 14, y + 5, 60 * frac, 6, 2).fill({
          color: frac > 0.35 ? Palette.playerCool : Palette.enemyHot,
        });
      } else if (hound) {
        // Down: hollow pip, pulsing — go get him.
        const pulse = 0.4 + 0.4 * Math.sin(this.time * 4);
        g.circle(x + 5, y + 8, 4.5).stroke({ width: 1.5, color: Palette.enemyHot, alpha: pulse + 0.2 });
        g.roundRect(x + 14, y + 5, 60, 6, 2).stroke({ width: 1, color: Palette.hudDim, alpha: 0.6 });
      }
    } else if (rs.character === 'barrow') {
      // Ironbound stacks (2026-07-28 clarity pass): 6 square pips — filled =
      // held; the NEWEST pip drains with its fade timer so the decay reads.
      const stacks = this.world.ironboundStacks;
      for (let i = 0; i < 6; i++) {
        const sx = x + i * 14;
        if (i < stacks) {
          const a = i === stacks - 1
            ? 0.45 + 0.55 * Math.max(0, this.world.ironboundFade) / IRONBOUND_STACK_SECONDS
            : 1;
          g.rect(sx, y + 4, 10, 10).fill({ color: Palette.enemyHotAlt, alpha: a });
        } else {
          g.rect(sx, y + 4, 10, 10).stroke({ width: 1.5, color: Palette.hudDim, alpha: 0.6 });
        }
      }
    }
  }

  /**
   * Wick only: screen-edge ember glow that grows as the candle dies — the
   * aspect's pressure readable without glancing at the bar. Pulses once the
   * guttering chip is live.
   */
  private syncVignette(w: number, h: number): void {
    const g = this.vignetteGfx;
    g.clear();
    if (this.runState.character !== 'wick') return;
    const burn = 1 - this.runState.candle;
    if (burn < 0.5) return;
    const strength = (burn - 0.5) / 0.5; // 0 at half candle → 1 at empty
    const pulse = this.runState.candle <= 0 ? 0.7 + 0.3 * Math.sin(this.time * 6) : 1;
    const thickness = 22 + 26 * strength;
    for (let i = 0; i < 3; i++) {
      const inset = (thickness / 3) * i;
      g.rect(inset, inset, w - 2 * inset, h - 2 * inset).stroke({
        width: thickness / 3 + 6,
        color: Palette.enemyHotAlt,
        alpha: 0.09 * strength * pulse * (1 - i / 3),
      });
    }
  }
}

/** Floor-event marker glyphs — each type must read distinctly at a glance. */
/** Centered, nearest-scaled prop sprite (ME5 pipeline). */
function propSprite(tex: Texture, label?: string): Sprite {
  const s = new Sprite(tex);
  s.anchor.set(0.5);
  if (label !== undefined) s.label = label;
  return s;
}

/**
 * Event markers: soft glow disc + a generated PNG prop where one exists
 * (ME5 — the '$' Text placeholders are gone). The vault door stays vector;
 * brazier markers carry BOTH flame states as labeled sprites, toggled in
 * render(). Trapped chests stay pixel-identical to loot chests by design.
 */
function drawEventMarker(type: FloorEventType, props: PropTextures): Container {
  const g = new Graphics();
  const color = EVENT_COLOR[type];
  for (const [r, a] of [[28, 0.06], [22, 0.1]] as const) {
    g.circle(0, 0, r).fill({ color, alpha: a });
  }
  if (type === 'vaultDoor') { // sealed door: heavy frame + keyhole
    g.roundRect(-11, -14, 22, 26, 4).fill({ color: 0x141a26 });
    g.roundRect(-11, -14, 22, 26, 4).stroke({ width: 2.5, color, alpha: 0.9 });
    g.circle(0, -3, 3).fill(color);
    g.rect(-1.5, -2, 3, 7).fill(color);
    return g;
  }
  if (type === 'brazierRelay') {
    const unlit = propSprite(props.brazierUnlit, 'unlit');
    const lit = propSprite(props.brazierLit, 'lit');
    lit.visible = false;
    g.addChild(unlit, lit);
    return g;
  }
  const tex =
    type === 'merchant' ? props.merchant
      : type === 'shrine' ? props.shrine
        : type === 'boneKey' ? props.boneKey
          : type === 'strongbox' ? props.strongbox
            : type === 'spring' ? props.spring
              : props.chest; // lootChest + ambush
  g.addChild(propSprite(tex));
  return g;
}

/** Poison vent grate (hazard emitter). */
function drawVentGrate(props: PropTextures): Container {
  return propSprite(props.vent);
}

/** Debris marker: cracked slab plus a faint lane showing the collapse path. */
function drawDebrisMarker(angle: number, halfLen: number, props: PropTextures): Container {
  const g = new Graphics();
  const dx = Math.cos(angle) * halfLen;
  const dy = Math.sin(angle) * halfLen;
  g.moveTo(-dx, -dy).lineTo(dx, dy)
    .stroke({ width: 4, color: Palette.debrisDeep, alpha: 0.28 });
  g.addChild(propSprite(props.debris));
  return g;
}

/** Gem geode: a boulder with a crystal seam — shoot it. */
function drawGeode(props: PropTextures): Container {
  const g = new Graphics();
  g.circle(0, 0, 16).fill({ color: Palette.gem, alpha: 0.05 });
  g.addChild(propSprite(props.geode));
  return g;
}

/** Cleansing altar: the generated obelisk-on-dais over a soft glow. */
function drawAltar(props: PropTextures): Container {
  const g = new Graphics();
  for (const [r, a] of [[34, 0.05], [26, 0.09]] as const) {
    g.circle(0, 0, r).fill({ color: Palette.playerCool, alpha: a });
  }
  g.addChild(propSprite(props.altar));
  return g;
}

/** Entrance shop: the merchant stall prop (the '$' placeholder is gone). */
function drawShop(props: PropTextures): Container {
  const g = new Graphics();
  for (const [r, a] of [[30, 0.07], [24, 0.12]] as const) {
    g.circle(0, 0, r).fill({ color: Palette.gold, alpha: a });
  }
  g.addChild(propSprite(props.merchant));
  return g;
}

function makeText(size: number, fill: number): Text {
  return new Text({
    text: '',
    style: { fontFamily: 'monospace', fontSize: size, fill },
  });
}
